import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PROVINCE_OPTIONS, citiesFor, provinceAnchor } from "@/data/canada_cities";
import { cityCoords, nearestCity } from "@/data/city_coords";

// Geo first browsing: detect where the visitor is, pick the nearest supported
// market, and let a saved choice (localStorage 'sr_market') beat geo forever.

const STORAGE_KEY = "sr_market";

export const MARKETS = [
  { city: "Toronto", province: "ON", lat: 43.6532, lng: -79.3832 },
  { city: "Vancouver", province: "BC", lat: 49.2827, lng: -123.1207 },
  { city: "Montreal", province: "QC", lat: 45.5019, lng: -73.5674 },
  { city: "Calgary", province: "AB", lat: 51.0447, lng: -114.0719 },
];

// All 13 provinces and territories, from the shared Canada-wide city dataset.
export const PROVINCES = PROVINCE_OPTIONS;

// Unknown Canadian city: fall back to the province's largest city.
export function marketForProvince(province) {
  const cities = citiesFor(province);
  if (cities.length) return { city: cities[0], province };
  return { city: "Toronto", province: "ON" };
}

export function marketCoords(market) {
  // Exact city coordinates first, so the map centers on Whistler when the
  // market is Whistler instead of jumping to the provincial anchor.
  const exact = cityCoords(market?.city, market?.province);
  if (exact) return exact;
  const m = MARKETS.find((x) => x.city === market?.city);
  if (m) return { lat: m.lat, lng: m.lng };
  // No exact coordinates for this city: center on its province's largest city.
  const anchor = provinceAnchor(market?.province);
  if (anchor) return anchor;
  return { lat: MARKETS[0].lat, lng: MARKETS[0].lng };
}

// stayType "short" shows short stay homes; "long" shows everything else.
export function matchesStay(listing, stayType) {
  if (stayType === "short") return listing?.stay_type === "short";
  return listing?.stay_type !== "short";
}

// Stable partition: listings in the chosen market first, everything else after.
// WHY case-insensitive: the deck and the backend match cities without case
// ("coquitlam" is "Coquitlam"), so the ordering here must agree with them.
export function marketFirst(list, city) {
  if (!city) return list;
  const want = city.trim().toLowerCase();
  const isIn = (l) => (l.city || "").trim().toLowerCase() === want;
  const inCity = list.filter(isIn);
  if (!inCity.length) return list;
  return [...inCity, ...list.filter((l) => !isIn(l))];
}

function readSaved() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

const LocationContext = createContext(null);

export function LocationProvider({ children }) {
  const saved = useMemo(readSaved, []);
  const hasSaved = !!saved?.market?.city;

  const [loc, setLoc] = useState(null);
  const [market, setMarketState] = useState(saved?.market || { city: "Toronto", province: "ON" });
  const [stayType, setStayTypeState] = useState(saved?.stayType === "short" ? "short" : "long");
  const [needsPicker, setNeedsPicker] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [chosen, setChosen] = useState(hasSaved);

  // Detection ladder, run ONLY when localStorage holds no saved market, so a
  // choice (manual or precisely detected) is never fought on later visits:
  //   1) browser geolocation (5s, silent on deny) snapped to the nearest
  //      supported city, then persisted because the fix is precise;
  //   2) IP lookup via /geo/ip snapped the same way, NOT persisted because
  //      IP location is coarse and a later visit may get real coordinates;
  //   3) both failed: keep the Toronto default and invite a manual pick.
  useEffect(() => {
    if (hasSaved) return; // a saved market always beats detection
    let alive = true;

    const ipFallback = async () => {
      try {
        const res = await api.get("/geo/ip", { timeout: 6000 });
        const geo = res.data && typeof res.data === "object" ? res.data : {};
        if (!alive) return;
        setLoc(geo);
        // Trust the IP-reported city NAME first: ip-api is usually accurate to
        // the suburb (e.g. Coquitlam). Snapping its coarse lat/lon to the
        // nearest city instead jumps a Coquitlam visitor to Vancouver. Only
        // fall back to coordinate-snapping when the reported city is unknown.
        if (geo.city && geo.region && cityCoords(geo.city, geo.region)) {
          setMarketState({ city: geo.city, province: geo.region });
          return;
        }
        if (typeof geo.lat === "number" && typeof geo.lon === "number") {
          const near = nearestCity(geo.lat, geo.lon);
          if (near) {
            setMarketState(near);
            return;
          }
        }
      } catch {
        // Endpoint unreachable: same as honest nulls, fall through.
      }
      if (alive) setNeedsPicker(true); // rung 3: Toronto stays, picker invites
    };

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (!alive) return;
          const near = nearestCity(pos.coords.latitude, pos.coords.longitude);
          if (near) {
            setMarketState(near);
            setChosen(true); // precise fix: persist so we stop re-detecting
          } else {
            ipFallback();
          }
        },
        () => alive && ipFallback(), // denied or unavailable: silently move on
        { timeout: 5000, maximumAge: 10 * 60 * 1000 }
      );
    } else {
      ipFallback();
    }

    return () => {
      alive = false;
    };
  }, [hasSaved]);

  // Persist only once the visitor makes an explicit choice.
  useEffect(() => {
    if (!chosen) return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ market, stayType }));
    } catch {
      // Storage blocked — browsing still works, the choice just does not stick.
    }
  }, [chosen, market, stayType]);

  const setMarket = useCallback((m) => {
    if (!m?.city) return;
    setMarketState({ city: m.city, province: m.province });
    setNeedsPicker(false);
    setChosen(true);
  }, []);

  const setStayType = useCallback((s) => {
    setStayTypeState(s === "short" ? "short" : "long");
    setChosen(true);
  }, []);

  const value = useMemo(
    () => ({ loc, market, setMarket, stayType, setStayType, needsPicker, pickerOpen, setPickerOpen }),
    [loc, market, setMarket, stayType, setStayType, needsPicker, pickerOpen]
  );

  return <LocationContext.Provider value={value}>{children}</LocationContext.Provider>;
}

export function useMarket() {
  const ctx = useContext(LocationContext);
  if (!ctx) {
    return {
      loc: null,
      market: { city: "Toronto", province: "ON" },
      setMarket: () => {},
      stayType: "long",
      setStayType: () => {},
      needsPicker: false,
      pickerOpen: false,
      setPickerOpen: () => {},
    };
  }
  return ctx;
}
